import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../global/supabase';
import { SUPABASE, CLIENT_ID, BRANDING } from '../config';

function AdminInsightsPanel() {
  const [insight, setInsight] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const [error, setError] = useState(null);
  const [expandedSections, setExpandedSections] = useState({ flags: true, unitStatus: false, trends: false, recommendations: false });
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const fetchLatestInsight = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: fetchErr } = await supabase
        .from('admin_insights')
        .select('*')
        .eq('deployment_id', CLIENT_ID)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();
      if (fetchErr && fetchErr.code !== 'PGRST116') throw fetchErr;
      setInsight(data || null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchLatestInsight(); }, [fetchLatestInsight]);

  const handleRegenerate = async () => {
    setRegenerating(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      const resp = await fetch(`${SUPABASE.url}/functions/v1/generate-daily-insights`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ deployment_id: CLIENT_ID }),
      });
      if (!resp.ok) throw new Error('Failed to regenerate insights');
      await fetchLatestInsight();
    } catch (err) {
      setError(err.message);
    } finally {
      setRegenerating(false);
    }
  };

  const handleStatusUpdate = async (newStatus) => {
    if (!insight) return;
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const { error: updateErr } = await supabase
        .from('admin_insights')
        .update({ status: newStatus, reviewed_by: user?.id, reviewed_at: new Date().toISOString() })
        .eq('id', insight.id);
      if (updateErr) throw updateErr;
      setInsight(prev => ({ ...prev, status: newStatus, reviewed_at: new Date().toISOString() }));
    } catch (err) {
      setError(err.message);
    }
  };

  const fetchHistory = async () => {
    setHistoryLoading(true);
    try {
      const fourteenDaysAgo = new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString();
      const { data, error: histErr } = await supabase
        .from('admin_insights')
        .select('id, created_at, summary, flags, status, model_used, tokens_used')
        .eq('deployment_id', CLIENT_ID)
        .gte('created_at', fourteenDaysAgo)
        .order('created_at', { ascending: false });
      if (histErr) throw histErr;
      setHistory(data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleShowHistory = () => {
    setShowHistory(true);
    fetchHistory();
  };

  const toggleSection = (key) => {
    setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const severityColors = { critical: '#d32f2f', warning: '#f57c00', info: '#1976d2' };
  const statusColors = { compliant: '#388e3c', needs_attention: '#f57c00', non_compliant: '#d32f2f' };
  const priorityColors = { high: '#d32f2f', medium: '#f57c00', low: '#1976d2' };
  const trendArrows = { positive: '\u25B2', negative: '\u25BC', neutral: '\u2014' };
  const trendColors = { positive: '#388e3c', negative: '#d32f2f', neutral: '#757575' };
  const insightStatusColors = { generated: '#1976d2', reviewed: '#388e3c', dismissed: '#757575' };

  if (loading) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <p style={{ color: '#666' }}>Loading AI Compliance Insights...</p>
      </div>
    );
  }

  if (error && !insight) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <p style={{ color: '#d32f2f' }}>Error: {error}</p>
        <button onClick={fetchLatestInsight} style={buttonStyle(BRANDING.primaryColor)}>Retry</button>
      </div>
    );
  }

  if (!insight) {
    return (
      <div style={{ padding: '20px', textAlign: 'center', background: '#f8f9fa', borderRadius: '8px', border: '1px solid #e0e0e0' }}>
        <p style={{ color: '#666', marginBottom: '15px' }}>No AI compliance insights generated yet.</p>
        <button onClick={handleRegenerate} disabled={regenerating} style={buttonStyle(BRANDING.primaryColor)}>
          {regenerating ? 'Generating...' : 'Generate Daily Report'}
        </button>
      </div>
    );
  }

  const flags = insight.flags || [];
  const trends = insight.trends || [];
  const unitStatus = insight.unit_status || {};
  const recommendations = insight.recommendations || [];
  const criticalCount = flags.filter(f => f.severity === 'critical').length;
  const warningCount = flags.filter(f => f.severity === 'warning').length;
  const infoCount = flags.filter(f => f.severity === 'info').length;

  return (
    <div style={{ background: '#fff', borderRadius: '8px', border: '1px solid #e0e0e0', overflow: 'hidden' }}>
      {/* Header */}
      <div style={{ background: BRANDING.primaryColor, color: '#fff', padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h3 style={{ margin: 0, fontSize: '16px' }}>AI Compliance Insights</h3>
          <p style={{ margin: '4px 0 0', fontSize: '12px', opacity: 0.9 }}>
            {new Date(insight.created_at).toLocaleString('en-US', { timeZone: 'America/New_York' })}
          </p>
        </div>
        <span style={{ background: insightStatusColors[insight.status] || '#999', color: '#fff', padding: '4px 10px', borderRadius: '12px', fontSize: '11px', textTransform: 'uppercase' }}>
          {insight.status}
        </span>
      </div>

      {/* Severity Badge Row */}
      <div style={{ display: 'flex', justifyContent: 'center', gap: '30px', padding: '15px 20px', background: '#f8f9fa', borderBottom: '1px solid #e0e0e0' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '24px', fontWeight: 'bold', color: severityColors.critical }}>{criticalCount}</div>
          <div style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase' }}>Critical</div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '24px', fontWeight: 'bold', color: severityColors.warning }}>{warningCount}</div>
          <div style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase' }}>Warning</div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '24px', fontWeight: 'bold', color: severityColors.info }}>{infoCount}</div>
          <div style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase' }}>Info</div>
        </div>
      </div>

      {/* Executive Summary */}
      <div style={{ padding: '20px' }}>
        <h4 style={{ margin: '0 0 8px', fontSize: '14px', color: '#333' }}>Executive Summary</h4>
        <p style={{ margin: 0, fontSize: '14px', lineHeight: '1.6', color: '#444' }}>{insight.summary}</p>
      </div>

      {/* Flags Section */}
      {flags.length > 0 && (
        <CollapsibleSection title={`Flags (${flags.length})`} expanded={expandedSections.flags} onToggle={() => toggleSection('flags')}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {flags.map((flag, i) => (
              <div key={i} style={{ padding: '12px', borderLeft: `4px solid ${severityColors[flag.severity] || '#999'}`, background: '#f8f9fa', borderRadius: '0 4px 4px 0' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                  <span style={{ background: severityColors[flag.severity] || '#999', color: '#fff', padding: '2px 8px', borderRadius: '3px', fontSize: '10px', textTransform: 'uppercase' }}>{flag.severity}</span>
                  <span style={{ fontSize: '12px', color: '#666' }}>{flag.unit}</span>
                </div>
                <div style={{ fontWeight: 'bold', fontSize: '13px', color: '#333', marginBottom: '2px' }}>{flag.title}</div>
                <div style={{ fontSize: '12px', color: '#555' }}>{flag.detail}</div>
              </div>
            ))}
          </div>
        </CollapsibleSection>
      )}

      {/* Unit Status Section */}
      {Object.keys(unitStatus).length > 0 && (
        <CollapsibleSection title="Unit Status" expanded={expandedSections.unitStatus} onToggle={() => toggleSection('unitStatus')}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
              <thead>
                <tr style={{ background: '#f1f1f1' }}>
                  <th style={{ padding: '8px', textAlign: 'left' }}>Unit</th>
                  <th style={{ padding: '8px', textAlign: 'left' }}>Status</th>
                  <th style={{ padding: '8px', textAlign: 'center' }}>Logs</th>
                  <th style={{ padding: '8px', textAlign: 'left' }}>Notes</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(unitStatus).map(([name, info], i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #eee' }}>
                    <td style={{ padding: '8px', fontWeight: 'bold' }}>{name}</td>
                    <td style={{ padding: '8px' }}>
                      <span style={{ background: statusColors[info.status] || '#999', color: '#fff', padding: '2px 8px', borderRadius: '3px', fontSize: '10px' }}>
                        {(info.status || '').replace('_', ' ')}
                      </span>
                    </td>
                    <td style={{ padding: '8px', textAlign: 'center' }}>{info.log_count ?? '-'}</td>
                    <td style={{ padding: '8px', fontSize: '11px', color: '#555' }}>{info.notes || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CollapsibleSection>
      )}

      {/* Trends Section */}
      {trends.length > 0 && (
        <CollapsibleSection title="Trends" expanded={expandedSections.trends} onToggle={() => toggleSection('trends')}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {trends.map((trend, i) => (
              <div key={i} style={{ padding: '8px 12px', fontSize: '13px' }}>
                <span style={{ color: trendColors[trend.type] || '#757575', fontWeight: 'bold', marginRight: '6px' }}>
                  {trendArrows[trend.type] || ''} {trend.title}
                </span>
                <span style={{ color: '#555' }}>{trend.detail}</span>
              </div>
            ))}
          </div>
        </CollapsibleSection>
      )}

      {/* Recommendations Section */}
      {recommendations.length > 0 && (
        <CollapsibleSection title="Recommendations" expanded={expandedSections.recommendations} onToggle={() => toggleSection('recommendations')}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {recommendations.map((rec, i) => (
              <div key={i} style={{ padding: '8px 12px', fontSize: '13px' }}>
                <span style={{ background: priorityColors[rec.priority] || '#999', color: '#fff', padding: '1px 6px', borderRadius: '3px', fontSize: '10px', textTransform: 'uppercase', marginRight: '8px' }}>{rec.priority}</span>
                <strong>{rec.title}</strong>: <span style={{ color: '#555' }}>{rec.detail}</span>
              </div>
            ))}
          </div>
        </CollapsibleSection>
      )}

      {/* Action Buttons */}
      <div style={{ padding: '16px 20px', borderTop: '1px solid #e0e0e0', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
        <button onClick={handleRegenerate} disabled={regenerating} style={buttonStyle(BRANDING.primaryColor)}>
          {regenerating ? 'Generating...' : 'Regenerate Daily'}
        </button>
        {insight.status !== 'reviewed' && (
          <button onClick={() => handleStatusUpdate('reviewed')} style={buttonStyle('#388e3c')}>Mark Reviewed</button>
        )}
        {insight.status !== 'dismissed' && (
          <button onClick={() => handleStatusUpdate('dismissed')} style={buttonStyle('#757575')}>Dismiss</button>
        )}
        <button onClick={handleShowHistory} style={buttonStyle('#1976d2')}>View History</button>
      </div>

      {/* Footer */}
      <div style={{ padding: '10px 20px', background: '#f8f9fa', borderTop: '1px solid #e0e0e0', fontSize: '11px', color: '#94a3b8' }}>
        Model: {insight.model_used || 'N/A'} | Tokens: {insight.tokens_used || 'N/A'} | Generated: {new Date(insight.created_at).toLocaleString('en-US', { timeZone: 'America/New_York' })}
      </div>

      {error && (
        <div style={{ padding: '10px 20px', background: '#fef2f2', color: '#d32f2f', fontSize: '12px', borderTop: '1px solid #fecaca' }}>
          {error}
        </div>
      )}

      {/* History Modal */}
      {showHistory && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: '8px', padding: '20px', maxWidth: '700px', width: '90%', maxHeight: '80vh', overflow: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ margin: 0, fontSize: '16px', color: '#333' }}>Insights History (Last 14 Days)</h3>
              <button onClick={() => setShowHistory(false)} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#666' }}>&times;</button>
            </div>
            {historyLoading ? (
              <p style={{ textAlign: 'center', color: '#666' }}>Loading...</p>
            ) : history.length === 0 ? (
              <p style={{ textAlign: 'center', color: '#666' }}>No insights found in the last 14 days.</p>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                <thead>
                  <tr style={{ background: '#f1f1f1' }}>
                    <th style={{ padding: '8px', textAlign: 'left' }}>Date</th>
                    <th style={{ padding: '8px', textAlign: 'center' }}>Critical</th>
                    <th style={{ padding: '8px', textAlign: 'center' }}>Warning</th>
                    <th style={{ padding: '8px', textAlign: 'center' }}>Info</th>
                    <th style={{ padding: '8px', textAlign: 'left' }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((h, i) => {
                    const hFlags = h.flags || [];
                    return (
                      <tr key={i} style={{ borderBottom: '1px solid #eee' }}>
                        <td style={{ padding: '8px' }}>{new Date(h.created_at).toLocaleDateString('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric' })}</td>
                        <td style={{ padding: '8px', textAlign: 'center', color: severityColors.critical, fontWeight: 'bold' }}>{hFlags.filter(f => f.severity === 'critical').length}</td>
                        <td style={{ padding: '8px', textAlign: 'center', color: severityColors.warning, fontWeight: 'bold' }}>{hFlags.filter(f => f.severity === 'warning').length}</td>
                        <td style={{ padding: '8px', textAlign: 'center', color: severityColors.info, fontWeight: 'bold' }}>{hFlags.filter(f => f.severity === 'info').length}</td>
                        <td style={{ padding: '8px' }}>
                          <span style={{ background: insightStatusColors[h.status] || '#999', color: '#fff', padding: '2px 6px', borderRadius: '3px', fontSize: '10px', textTransform: 'uppercase' }}>{h.status}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Collapsible Section Component
function CollapsibleSection({ title, expanded, onToggle, children }) {
  return (
    <div style={{ borderTop: '1px solid #e0e0e0' }}>
      <button onClick={onToggle} style={{ width: '100%', padding: '12px 20px', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '14px', fontWeight: '600', color: '#333' }}>
        {title}
        <span style={{ fontSize: '12px', color: '#666' }}>{expanded ? '\u25B2' : '\u25BC'}</span>
      </button>
      {expanded && <div style={{ padding: '0 20px 16px' }}>{children}</div>}
    </div>
  );
}

// Button style helper
function buttonStyle(bg) {
  return {
    background: bg, color: '#fff', border: 'none', padding: '8px 16px',
    borderRadius: '4px', cursor: 'pointer', fontSize: '12px', fontWeight: '600',
    textTransform: 'uppercase', letterSpacing: '0.5px',
  };
}

export default AdminInsightsPanel;
